import axios, {
  AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios'
import { NetworkError } from '~/errors/NetworkError'
import type { CaptureErrorContext } from '~/utils/errors'

export interface HttpOptions<ResponseT = any>
  extends AxiosRequestConfig<ResponseT> {
  body?: ResponseT
  query?: Record<string, string>
}

type HttpReturnType<ResponseT> = Promise<
  | {
      data: ResponseT
      error: null
      status: number
      statusText: string
    }
  | {
      data: null
      error: Error
      status: number
      statusText: string
    }
>

const MAX_CAPTURED_BODY_LENGTH = 2000

// Collapse id path segments (UUIDs, Mongo ObjectIds, numeric ids) so events
// tag by endpoint shape, not by individual record.
function normalizeEndpoint(url: string): string {
  return url
    .split('?')[0]
    .split('/')
    .map((segment) =>
      /^([0-9a-f]{24}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+)$/i.test(
        segment,
      )
        ? ':id'
        : segment,
    )
    .join('/')
}

function truncate(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined
  try {
    const serialized = typeof value === 'string' ? value : JSON.stringify(value)
    return serialized.length > MAX_CAPTURED_BODY_LENGTH
      ? `${serialized.slice(0, MAX_CAPTURED_BODY_LENGTH)}…[truncated]`
      : serialized
  } catch {
    return '[unserializable]'
  }
}

// Query params can carry user-typed content (search text, emails). Numbers
// and booleans (pagination, flags) are safe to keep; everything else is
// reduced to its key so the event shows WHICH params were set, not values.
function redactParams(
  params: unknown,
): Record<string, string | number | boolean> | undefined {
  if (!params || typeof params !== 'object') return undefined
  return Object.fromEntries(
    Object.entries(params).map(([key, value]) => [
      key,
      typeof value === 'number' || typeof value === 'boolean'
        ? value
        : '[redacted]',
    ]),
  )
}

// Only the API's error envelope fields are attached — an arbitrary response
// body can echo user-provided or confidential content. Non-object bodies
// (e.g. a proxy's HTML error page) are described, never included.
function redactResponseBody(
  data: unknown,
): Record<string, unknown> | undefined {
  if (data === undefined || data === null) return undefined
  if (typeof data !== 'object') {
    return { bodyType: typeof data, bodyLength: String(data).length }
  }
  const { statusCode, error, message, timestamp } = data as Record<
    string,
    unknown
  >
  return {
    statusCode,
    error: truncate(error),
    message: truncate(message),
    timestamp,
  }
}

// Context-only enrichment: what request failed, how it failed (error.code
// separates timeouts, cancellations, and connection failures inside the
// generic "Network Error" bucket), and what the server said. Deliberately no
// filtering, level changes, or fingerprinting — the event stream must stay
// comparable with historical data while we analyse it.
function axiosErrorContext(error: AxiosError): CaptureErrorContext {
  const method = (error.config?.method || 'get').toUpperCase()
  const status = error.response?.status

  return {
    tags: {
      'api.method': method,
      'api.endpoint': normalizeEndpoint(error.config?.url || 'unknown'),
      'api.status': status ?? 'no-response',
      'api.code': error.code || 'unknown',
    },
    contexts: {
      'API Request': {
        method,
        url: [error.config?.baseURL, error.config?.url]
          .filter(Boolean)
          .join(''),
        params: redactParams(error.config?.params),
      },
      'API Response': {
        status,
        statusText: error.response?.statusText,
        body: redactResponseBody(error.response?.data),
      },
    },
  }
}

export class HttpService {
  private readonly baseOptions: AxiosRequestConfig

  constructor(url: string, options: Partial<HttpOptions>) {
    const { body, query, ...rest } = options
    this.baseOptions = {
      baseURL: url,
      method: 'get',
      data: body,
      params: query,
      ...rest,
    }
  }

  async request<ResponseT>(
    url: string,
    options?: Partial<HttpOptions>,
  ): HttpReturnType<ResponseT> {
    let response: AxiosResponse<ResponseT> | null = null
    try {
      response = await axios<ResponseT>({
        ...this.baseOptions,
        ...options,
        url,
      })

      return {
        data: response.data,
        error: null,
        status: response.status,
        statusText: response.statusText,
      }
    } catch (error) {
      captureError(
        error as Error,
        error instanceof AxiosError ? axiosErrorContext(error) : undefined,
      )
      let networkError: NetworkError | null = null
      if (error instanceof AxiosError) {
        networkError = NetworkError.fromAxiosError(error)
      } else {
        networkError = new NetworkError({
          statusCode: 500,
          error: 'Unknown Error',
          message: 'Unknown Error',
          cause: error,
        })
      }

      return {
        data: null,
        error: networkError,
        status: response?.status || 500,
        statusText: response?.statusText || 'Unknown Error',
      }
    }
  }
}
